import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom"; // Assuming you're using React Router

// CRM API Configuration
const CRM_ACCESS_TOKEN = "nbcLc99ivtoh5FO8xqGEOZEW6ddcb4szdtvHdVNCeTh980VQNehbfScBK0MvbQTb";
const CRM_LIST_ID = "6a0d6cae1f81ea6acd49bfba";
const CRM_API_BASE_URL = "https://enterprise.godial.cc/meta/api/externals/contact/add";

const FormModal = (props) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formError, setFormError] = useState(false);
  const navigate = useNavigate();

  // Yup validation schema
  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    phone: Yup.string()
      .required("Phone is required")
      .matches(/^\d{10}$/, "Phone number must be 10 digits long"),
  });

  // Formik setup
  const formik = useFormik({
    initialValues: {
      name: "",
      phone: "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      const formData = new FormData();
      formData.append("Name", values.name);
      formData.append("Phone", values.phone);

      try {
        setIsSubmitted(true);

        // Send to Google Sheets (using no-cors mode to bypass CORS restrictions)
        // Note: With no-cors, we cannot read the response, but the request will still be sent
        fetch(
          "https://script.google.com/macros/s/AKfycby2oBYtD7s0QOS9rvkWDAj1dRJOiHhh9miQFdmuv-skghzsBIR5AVKv0Ji8AFSvJbVA/exec",
          {
            method: "POST",
            mode: "no-cors",
            body: formData,
          }
        ).catch((err) => {
          console.warn("Google Sheets submission error (non-blocking):", err);
        });

        // Send data to CRM API - try multiple formats based on error messages
        let crmResponse;
        let crmSuccess = false;
        let lastError = null;

        // Try Format 1: listId in body (original format - error said "listId is a required argument")
        try {
          const crmDataFormat1 = {
            name: values.name,
            phone: values.phone,
            listId: CRM_LIST_ID,
            redirect: "",
            source: "website",
            campaign: "landing-page",
          };
          
          crmResponse = await fetch(
            `${CRM_API_BASE_URL}?access_token=${CRM_ACCESS_TOKEN}`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(crmDataFormat1),
            }
          );
          
          if (crmResponse.ok) {
            crmSuccess = true;
            console.log("✅ CRM data sent successfully (Format 1: listId in body)");
          } else {
            const errorText = await crmResponse.text();
            lastError = { status: crmResponse.status, response: errorText };
            console.warn("❌ CRM Format 1 failed:", lastError);
          }
        } catch (err) {
          lastError = err;
          console.warn("❌ CRM Format 1 error:", err);
        }

        // Try Format 2: listId in body + id in body (error said "Model::findById requires the id argument")
        if (!crmSuccess) {
          try {
            const crmDataFormat2 = {
              name: values.name,
              phone: values.phone,
              id: CRM_LIST_ID,
              listId: CRM_LIST_ID,
              redirect: "",
              source: "website",
              campaign: "landing-page",
            };
            
            crmResponse = await fetch(
              `${CRM_API_BASE_URL}?access_token=${CRM_ACCESS_TOKEN}`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify(crmDataFormat2),
              }
            );
            
            if (crmResponse.ok) {
              crmSuccess = true;
              console.log("✅ CRM data sent successfully (Format 2: listId + id in body)");
            } else {
              const errorText = await crmResponse.text();
              lastError = { status: crmResponse.status, response: errorText };
              console.warn("❌ CRM Format 2 failed:", lastError);
            }
          } catch (err) {
            lastError = err;
            console.warn("❌ CRM Format 2 error:", err);
          }
        }

        // Try Format 3: listId in URL + id in body
        if (!crmSuccess) {
          try {
            const crmDataFormat3 = {
              name: values.name,
              phone: values.phone,
              id: CRM_LIST_ID,
              redirect: "",
              source: "website",
              campaign: "landing-page",
            };
            
            crmResponse = await fetch(
              `${CRM_API_BASE_URL}?access_token=${CRM_ACCESS_TOKEN}&listId=${CRM_LIST_ID}`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify(crmDataFormat3),
              }
            );
            
            if (crmResponse.ok) {
              crmSuccess = true;
              console.log("✅ CRM data sent successfully (Format 3: listId in URL + id in body)");
            } else {
              const errorText = await crmResponse.text();
              lastError = { status: crmResponse.status, response: errorText };
              console.error("❌ CRM API Error Details:", {
                status: crmResponse.status,
                statusText: crmResponse.statusText,
                response: errorText
              });
            }
          } catch (err) {
            lastError = err;
            console.error("❌ CRM API Error:", err);
          }
        }

        // Check CRM response
        if (crmSuccess) {
          console.log("✅ Data sent successfully to CRM.");
          // Reset form and close modal
          formik.resetForm({ name: "", phone: ""});
          props.onClose();
          navigate("/thank-you");
        } else {
          setIsSubmitted(false);
          setFormError(true);
          console.error("❌ CRM submission failed. Last error:", lastError);
          // Don't navigate - let user try again
        }
        
      } catch (error) {
        setIsSubmitted(false);
        setFormError(true);
        console.error("❌ Error:", error);
      }
    },
  });

  useEffect(() => {
    if (props.open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [props.open]);

  // console.log("Formik", formik.values);

  return (
    <div
      onClick={props.onClose}
      className={`
      fixed inset-0 z-50 flex items-center justify-center transition-colors 
      ${props.open ? "visible bg-black/70" : "invisible"}
    `}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`
        fixed inset-0 z-50 flex items-center justify-center
        ${props.open ? "scale-100 opacity-100" : "scale-125 opacity-0"}
      `}
        style={{ overflowY: "auto", padding: "100px 20px 0" }}
      >
        <div
          className="relative flex flex-col md:flex-row w-full max-w-2xl mx-auto max-h-[80vh] rounded-md bg-white p-6 px-12 shadow-lg transition-all"
          style={{ border: "1px solid #888", overflowY: "auto" }}
        >
          <button
            onClick={props.onClose}
            className="absolute right-2 top-2 rounded-lg bg-white p-1 text-gray-400 hover:bg-gray-50 hover:text-gray-600"
          >
            <img
              className="cross-img"
              src="images/cross-img-removebg-preview.png"
              width="25px"
              height="30px"
              alt="cross"
            />
          </button>

          <div className="md:w-2/3 flex flex-col items-start">
            <h2 className="text-start text-xl font-bold text-black md:text-2xl">
              {props.formTitle}
            </h2>
            {!isSubmitted ? (
              <p className="max-w-sm text-start text-medium sm:text-base sm:font-medium my-3">
                {props.formSubTitle}
              </p>
            ) : (
              ""
            )}

            <div className="w-full text-center flex items-center justify-center lg:h-screen">
              {/* Form Wrapper */}
              {isSubmitted && !formError ? (
                <div className="text-center bg-[#278f47] text-[#ffff00] mx-4 p-4 border-1 border-[#520808]">
                  <h4 className="text-2xl">Successfully Submitted</h4>
                  <h3 className="text-xl">
                    Thank you! We will contact you soon.
                  </h3>
                </div>
              ) : formError ? (
                <div className="text-center bg-[#ffff00] text-[#E72744] mx-4 p-4 border-1 border-[#520808]">
                  <p className="text-2xl">
                    Something went wrong. Please try again.
                  </p>
                </div>
              ) : (
                <form onSubmit={formik.handleSubmit}>
                  <div className="my-3 flex flex-col justify-center gap-4">
                    <div className="flex flex-col gap-2">
                      <label
                        className="text-lg text-start font-medium text-gray-600"
                        htmlFor="inputName"
                      >
                        Name
                      </label>
                      <input
                        id="inputName"
                        className="border-b border-gray-300 p-2 focus:border-blue-500 focus:outline-none"
                        type="text"
                        name="name"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.name && formik.errors.name && (
                        <span className="text-red-500 text-sm">
                          {formik.errors.name}
                        </span>
                      )}
                    </div>

                    <div className="flex flex-col gap-2">
                      <label
                        className="text-lg text-start font-medium text-gray-600"
                        htmlFor="inputPhone"
                      >
                        Number
                      </label>
                      <input
                        id="inputPhone"
                        className="border-b border-gray-300 p-2 focus:border-blue-500 focus:outline-none"
                        type="text"
                        name="phone"
                        value={formik.values.phone}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.phone && formik.errors.phone && (
                        <span className="text-red-500 text-sm">
                          {formik.errors.phone}
                        </span>
                      )}
                    </div>

                    <button type="submit" className="relative w-full sm:w-4/5 h-[50px] sm:h-[45px] lg:h-[50px] border-none outline-none text-white bg-[#05335f] cursor-pointer z-0 rounded-lg m-2 mt-3 text-sm sm:text-base lg:text-lg font-bold transition duration-300 hover:text-[#05335f] glow-on-hover">
                      {props.buttonTitle}
                    </button>
                  </div>
                </form>
              )}

              {/* End of Form Wrapper */}
            </div>
          </div>

          {/* Images Section */}
          <div className="md:w-1/3 hidden md:flex flex-col items-center justify-center gap-4">
            <div className="flex flex-col items-center">
              <img
                src="images/free-pickup3-removebg-preview.png"
                alt="free-pickup"
                className="w-24 h-auto rounded-md"
              />
              <p className="svg-p">Free Site Assistance</p>
            </div>
            <div className="flex flex-col items-center">
              <img
                src="images/instant-callback-removebg-preview.png"
                alt="instant-callback"
                className="w-24 h-auto rounded-md"
              />
              <p className="svg-p">Instant Call Back</p>
            </div>
            <div className="flex flex-col items-center">
              <img
                src="images/best.png"
                alt="best offers"
                className="w-24 h-auto rounded-md"
              />
              <p className="svg-p">Best-Offers</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormModal;
